import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, TrendingUp, Eye, MousePointerClick, Loader2, Globe, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SOURCE_LABELS = {
  'for-agents': '/for-agents (direct)',
  'vs-rightmove': '/vs-rightmove (UK SEO)',
  'vs-99acres': '/vs-99acres (IN SEO)',
  'vs-realtor': '/vs-realtor (CA SEO)',
  'landing-hero': 'Landing page hero',
};

const CTA_LABELS = {
  'page-view': 'Page Views',
  'register': 'Clicked "Start Free"',
  'share-whatsapp': 'Shared on WhatsApp',
  'view-ambassador': 'Viewed Ambassador',
  'copy-text': 'Copied Share Text',
};

export const AdminSalesTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/sales/admin/funnel?days=${days}`);
      setData(res.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center justify-center py-12" data-testid="admin-sales-loading"><Loader2 className="h-6 w-6 animate-spin text-[#E07A3A]" /></div>;
  }
  if (!data) return <p className="text-slate-500 text-sm">Unable to load sales data.</p>;

  return (
    <div className="space-y-6" data-testid="admin-sales-tab">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-1 block uppercase font-medium flex items-center gap-2">
            <Zap className="h-3 w-3" /> Agent Acquisition Funnel
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Sales Generated</h2>
          <p className="text-sm text-slate-500 mt-1">Track every lead from `/for-agents`, competitor-alias pages, and WhatsApp shares.</p>
        </div>
        <div className="flex gap-1 border border-slate-200">
          {[7, 30, 90, 365].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-2 text-xs tracking-widest uppercase font-bold transition-colors ${days === d ? 'bg-[#0F172A] text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
              data-testid={`sales-range-${d}`}
            >
              {d === 365 ? '1Y' : `${d}D`}
            </button>
          ))}
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Metric icon={<Eye className="h-4 w-4" />} label="Page Views" value={data.page_views} testid="metric-page-views" />
        <Metric icon={<MousePointerClick className="h-4 w-4" />} label="CTA Clicks" value={data.cta_clicks} testid="metric-cta-clicks" highlight />
        <Metric icon={<Users className="h-4 w-4" />} label="Agent Signups" value={data.registrations} testid="metric-registrations" highlight />
        <Metric icon={<TrendingUp className="h-4 w-4" />} label="Conversion %" value={`${data.conversion_rate_pct}%`} testid="metric-conversion" />
        <Metric icon={<Globe className="h-4 w-4" />} label="Total Leads" value={data.total_leads} testid="metric-total-leads" />
      </div>

      {/* By source */}
      <div className="luxury-card p-6" data-testid="sales-by-source">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4 tracking-wider uppercase">Leads by Source</h3>
        {Object.keys(data.by_source).length === 0 ? (
          <p className="text-sm text-slate-400 italic">No traffic captured yet. Start sharing `/for-agents` on WhatsApp to collect data.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(data.by_source).map(([src, ctas]) => {
              const total = Object.values(ctas).reduce((a, b) => a + b, 0);
              const views = ctas['page-view'] || 0;
              const conversions = (ctas['register'] || 0);
              const rate = views ? Math.round((conversions / views) * 100) : 0;
              return (
                <div key={src} className="border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <p className="font-semibold text-[#0F172A] text-sm">{SOURCE_LABELS[src] || src}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-slate-500">Total: <span className="font-bold text-[#0F172A]">{total}</span></span>
                      <span className="text-[#E07A3A] font-bold">{rate}% CTR</span>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-5 gap-2">
                    {Object.entries(ctas).map(([cta, count]) => (
                      <div key={cta} className="bg-slate-50 px-2 py-1.5 text-[10px]">
                        <p className="text-slate-400 tracking-wider uppercase mb-0.5">{CTA_LABELS[cta] || cta}</p>
                        <p className="font-bold text-[#0F172A]">{count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* By country */}
      <div className="luxury-card p-6" data-testid="sales-by-country">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4 tracking-wider uppercase">Leads by Country</h3>
        <div className="grid grid-cols-3 gap-3">
          {['UK', 'IN', 'CA'].map(code => {
            const count = data.by_country[code] || 0;
            const total = Object.values(data.by_country).reduce((a, b) => a + b, 0);
            const pct = total ? Math.round((count / total) * 100) : 0;
            return (
              <div key={code} className="border border-slate-200 p-4 text-center">
                <p className="text-2xl font-['Playfair_Display'] text-[#E07A3A] font-bold">{count}</p>
                <p className="text-[10px] tracking-wider uppercase text-slate-500 mt-1">{code} · {pct}%</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent leads */}
      <div className="luxury-card p-6" data-testid="sales-recent">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4 tracking-wider uppercase">Recent Activity (Last 20)</h3>
        {data.recent_leads.length === 0 ? (
          <p className="text-sm text-slate-400 italic">Nothing tracked yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[10px] tracking-wider uppercase text-slate-400">
                  <th className="py-2">When</th>
                  <th className="py-2">Source</th>
                  <th className="py-2">Country</th>
                  <th className="py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_leads.map((lead, i) => (
                  <tr key={`${lead.created_at}-${i}`} className="border-b border-slate-100 last:border-0">
                    <td className="py-2.5 text-xs text-slate-500">{lead.created_at ? new Date(lead.created_at).toLocaleString() : '-'}</td>
                    <td className="py-2.5 text-xs text-[#1A1A1A]">{SOURCE_LABELS[lead.source] || lead.source}</td>
                    <td className="py-2.5 text-xs uppercase tracking-wider text-slate-500">{lead.country}</td>
                    <td className="py-2.5 text-xs">
                      <span className={`px-2 py-0.5 tracking-wider uppercase font-bold rounded border text-[10px] ${lead.cta === 'register' ? 'bg-[#0F172A] text-yellow-300 border-yellow-500/60' : lead.cta === 'share-whatsapp' ? 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50' : 'bg-slate-900 text-slate-300 border-slate-500/40'}`}>
                        {CTA_LABELS[lead.cta] || lead.cta}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Metric = ({ icon, label, value, testid, highlight }) => (
  <div className={`luxury-card p-4 ${highlight ? 'border-[#E07A3A]/30' : ''}`} data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-400 text-[10px] tracking-[0.2em] uppercase font-bold mb-1">
      {icon}
      {label}
    </div>
    <p className="text-2xl font-['Playfair_Display'] font-semibold text-[#E07A3A]">{value}</p>
  </div>
);

export default AdminSalesTab;
